export const U2000FormatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const pad = (num: number) => String(num).padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Mois = 0-indexé
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/** Fuseau d'affichage des dates métier (aligné backend U2000Validations). */
export const U2000_ORG_DISPLAY_TIMEZONE = "America/Montreal";

/** Fuseau actif alimenté par initLU.organizationTimezone (défaut Montréal). */
let u2000ActiveDisplayTimezone = U2000_ORG_DISPLAY_TIMEZONE;

/** Met à jour le fuseau d'affichage actif (appelé depuis les contextes module au chargement initLU). */
export function u2000SetActiveDisplayTimezone(timeZone: string | null | undefined): void {
  u2000ActiveDisplayTimezone = u2000ResolveTimezone(timeZone ?? U2000_ORG_DISPLAY_TIMEZONE);
}

export function u2000GetActiveDisplayTimezone(): string {
  return u2000ActiveDisplayTimezone;
}

export type U2000WallClockParts = {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
};

function u2000ValidateTimezone(timeZone: string): string {
  const tz = timeZone.trim();
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return tz;
  } catch {
    return U2000_ORG_DISPLAY_TIMEZONE;
  }
}

/** Valide un identifiant IANA ; sans argument lit le fuseau actif ; repli sur Montréal si invalide. */
export function u2000ResolveTimezone(timeZone?: string | null): string {
  const tz = (timeZone ?? u2000ActiveDisplayTimezone).trim();
  return u2000ValidateTimezone(tz);
}

/** Lit l'heure murale (parties civiles) d'un instant UTC dans un fuseau donné. */
export function u2000ReadWallClock(utcMs: number, timeZone: string): U2000WallClockParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: u2000ResolveTimezone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(utcMs));
  const n = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  return { y: n("year"), mo: n("month"), d: n("day"), h: n("hour"), mi: n("minute"), s: n("second") };
}

function u2000WallPartsToPseudoUtcMs(parts: U2000WallClockParts): number {
  return Date.UTC(parts.y, parts.mo - 1, parts.d, parts.h, parts.mi, parts.s);
}

/** Convertit une heure murale (dans un fuseau) en instant UTC (ms). */
export function u2000WallClockToUtcMs(want: U2000WallClockParts, timeZone: string): number {
  const tz = u2000ResolveTimezone(timeZone);
  let utcMs = u2000WallPartsToPseudoUtcMs(want);
  for (let i = 0; i < 8; i++) {
    const got = u2000ReadWallClock(utcMs, tz);
    if (
      got.y === want.y &&
      got.mo === want.mo &&
      got.d === want.d &&
      got.h === want.h &&
      got.mi === want.mi &&
      got.s === want.s
    ) {
      return utcMs;
    }
    utcMs += u2000WallPartsToPseudoUtcMs(want) - u2000WallPartsToPseudoUtcMs(got);
  }
  return utcMs;
}

/** Formate les parties civiles en « yyyy-MM-dd ». */
export function u2000FormatYmdFromParts(parts: Pick<U2000WallClockParts, "y" | "mo" | "d">): string {
  return `${String(parts.y).padStart(4, "0")}-${String(parts.mo).padStart(2, "0")}-${String(parts.d).padStart(2, "0")}`;
}

/** Jour civil org courant (yyyy-MM-dd). */
export function u2000TodayOrgYmd(timeZone?: string): string {
  return u2000FormatYmdFromParts(u2000ReadWallClock(Date.now(), u2000ResolveTimezone(timeZone)));
}

/** Ajoute `dayOffset` jours à une date `yyyy-MM-dd` (arithmétique jour civil pur). */
export function u2000ShiftCivilYmd(ymd: string, dayOffset: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const utcMs = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + dayOffset);
  const d = new Date(utcMs);
  return u2000FormatYmdFromParts({ y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, d: d.getUTCDate() });
}

/** Lundi de la semaine civile contenant `ymd` (yyyy-MM-dd). */
export function u2000MondayOfCivilWeekYmd(ymd: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const dow = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  return u2000ShiftCivilYmd(ymd, diffToMonday);
}

/** Lundi de la semaine civile courante (fuseau org). */
export function u2000CurrentWeekMondayYmd(timeZone?: string): string {
  const today = u2000TodayOrgYmd(timeZone);
  return u2000MondayOfCivilWeekYmd(today) ?? today;
}

/** Lundi de la semaine civile précédente (fuseau org). */
export function u2000PreviousWeekMondayYmd(timeZone?: string): string {
  const monday = u2000CurrentWeekMondayYmd(timeZone);
  return u2000ShiftCivilYmd(monday, -7) ?? monday;
}

function u2000CivilYmdToUtcNoonMs(ymd: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
}

/** Libellé long d'un jour civil (ex. « 23 juin 2026 ») dans le fuseau org. */
export function u2000FormatCivilYmdLong(ymd: string, locale?: string, options?: Intl.DateTimeFormatOptions): string {
  const utcMs = u2000CivilYmdToUtcNoonMs(ymd);
  if (utcMs == null) return ymd;
  return new Intl.DateTimeFormat(locale?.trim() || undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: u2000ResolveTimezone(),
    ...options,
  }).format(new Date(utcMs));
}

/** Plage « lundi … – dimanche … » pour un lundi civil (avec nom du jour si demandé). */
export function u2000FormatCivilWeekRangeLong(mondayYmd: string, locale?: string, includeWeekday = true): string {
  const sundayYmd = u2000ShiftCivilYmd(mondayYmd, 6);
  if (!sundayYmd) return mondayYmd;
  const fmtOpts: Intl.DateTimeFormatOptions = includeWeekday
    ? { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    : { day: "numeric", month: "long", year: "numeric" };
  return `${u2000FormatCivilYmdLong(mondayYmd, locale, fmtOpts)} – ${u2000FormatCivilYmdLong(sundayYmd, locale, fmtOpts)}`;
}

/** Plage période feuille de temps pour fil d'Ariane (sans nom du jour de la semaine). */
export function u2000FormatTimesheetPeriodBreadcrumb(periodDate: string, locale: string): string {
  if (!periodDate?.trim()) return "";
  const mondayYmd = U2000ApiValueToDisplayYmd(periodDate);
  if (!mondayYmd) return periodDate.trim();
  return u2000FormatCivilWeekRangeLong(mondayYmd, locale, false);
}

/**
 * Jour civil yyyy-MM-dd pour affichage, sans décalage du fuseau local du navigateur.
 * - Date seule « yyyy-MM-dd » : telle quelle.
 * - Instant ISO : jour civil dans le fuseau d'affichage org.
 */
export function U2000ApiValueToDisplayYmd(value: unknown): string | null {
  if (value == null || value === "") return null;

  const s = String(value).trim();
  if (!s) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return s;
  }

  const d = value instanceof Date ? value : new Date(s);
  if (Number.isNaN(d.getTime())) return null;

  return u2000FormatYmdFromParts(u2000ReadWallClock(d.getTime(), u2000ResolveTimezone()));
}
